import { useState, useMemo } from "react";
import { Box, Button, Divider, List, Typography, Alert } from "@mui/material";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import StorageIcon from "@mui/icons-material/Storage";
import DescriptionIcon from "@mui/icons-material/Description";
import type { GlobalSearchResult } from "../types/globalSearch.types";
import GlobalSearchResultItem from "./GlobalSearchResultItem";
import { useSearchResultChat } from "../hooks/useSearchResultChat";
import { useGlobalSearchStore } from "../stores/GlobalSearchStore";

interface ResultsProps {
  results: GlobalSearchResult[];
  selectedIndex: number;
  onResultClick: (index: number) => void;
  searchQuery: string;
}

export default function GlobalSearchResults({
  results,
  selectedIndex,
  onResultClick,
  searchQuery,
}: ResultsProps) {
  const { chatWithResult, chatWithResults } = useSearchResultChat();
  const { closeSearch } = useGlobalSearchStore();
  const [showTypeSelection, setShowTypeSelection] = useState(false);

  // Analyze result types
  const { hasDatasets, hasDocuments, datasetCount, documentCount } =
    useMemo(() => {
      const datasets = results.filter((r) => r.type === "dataset");
      const documents = results.filter((r) => r.type === "chunk");
      return {
        hasDatasets: datasets.length > 0,
        hasDocuments: documents.length > 0,
        datasetCount: datasets.length,
        documentCount: documents.length,
      };
    }, [results]);

  const hasMixedTypes = hasDatasets && hasDocuments;

  const handleChatWithResult = (result: GlobalSearchResult) => {
    closeSearch();
    chatWithResult(result, searchQuery);
  };

  const handleChatWithAll = () => {
    if (hasMixedTypes) {
      // Show selection UI instead of immediately chatting
      setShowTypeSelection(true);
    } else {
      // Only one type, proceed directly
      closeSearch();
      chatWithResults(results, searchQuery);
    }
  };

  const handleChatWithDocuments = () => {
    const documentResults = results.filter((r) => r.type === "chunk");
    closeSearch();
    chatWithResults(documentResults, searchQuery);
  };

  const handleChatWithDatasets = () => {
    const datasetResults = results.filter((r) => r.type === "dataset");
    closeSearch();
    chatWithResults(datasetResults, searchQuery);
  };

  const handleCancelSelection = () => {
    setShowTypeSelection(false);
  };

  return (
    <Box>
      {/* Type selection UI - shown when mixed results and user clicked chat */}
      {showTypeSelection && hasMixedTypes && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "action.hover",
          }}
        >
          <Alert
            severity="info"
            sx={{ mb: 1.5, py: 0.5 }}
            data-testid="global-search-mixed-results-alert"
          >
            Results include both datasets and documents. Chat can only work with
            one type at a time.
          </Alert>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Choose what to chat about:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleChatWithDocuments}
              data-testid="global-search-chat-documents"
              data-attr="global-search:btn:chat-with-documents"
            >
              Chat with Documents ({documentCount})
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<StorageIcon />}
              onClick={handleChatWithDatasets}
              data-testid="global-search-chat-datasets"
              data-attr="global-search:btn:chat-with-datasets"
            >
              Chat with Datasets ({datasetCount})
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={handleCancelSelection}
              data-testid="global-search-cancel-selection"
              data-attr="global-search:btn:cancel-type-selection"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {/* Chat with all results button */}
      {results.length > 1 && !showTypeSelection && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {results.length} results
            {hasMixedTypes && (
              <Typography
                component="span"
                variant="caption"
                color="warning.main"
                sx={{ ml: 1 }}
              >
                (mixed types)
              </Typography>
            )}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ChatOutlinedIcon />}
            onClick={handleChatWithAll}
            data-testid="global-search-chat-all"
            data-attr="global-search:btn:chat-with-all-results"
          >
            Chat with results
          </Button>
        </Box>
      )}

      <List
        disablePadding
        data-testid="global-search-results"
        data-attr="global-search:menu:results-list"
      >
        {results.map((result, index) => (
          <div key={result.id}>
            {index > 0 && <Divider component="li" />}
            <GlobalSearchResultItem
              result={result}
              isSelected={index === selectedIndex}
              onClick={() => onResultClick(index)}
              onChatClick={() => handleChatWithResult(result)}
              index={index}
            />
          </div>
        ))}
      </List>
    </Box>
  );
}
